import React from 'react';
import { Callout, DefaultButton } from '@fluentui/react';
import { IGlobalStorefrontSettings, IStorefrontSettings } from '../../../../models/Storefront';
import ImageEditor from '../../../Inputs/ImageEditor';

// The footer logo counterpart to StorefrontHeaderLogoEditor: click the logo (or its placeholder)
// in edit mode to upload/replace/remove it. The storefront falls back to the organization's
// global footer logo when the store's own is empty (StorefrontSettings.MergeWithGlobal), so the
// preview shows the inherited logo and the callout says where it came from.

interface IStorefrontFooterLogoEditorProps {
    storefrontSettings: IStorefrontSettings;
    globalSettings?: IGlobalStorefrontSettings;
    onStorefrontSettingsChange: (newSettings: IStorefrontSettings) => void;
    editMode?: boolean;
}

const StorefrontFooterLogoEditor: React.FC<IStorefrontFooterLogoEditorProps> = (props) => {
    const targetRef = React.useRef<HTMLDivElement>(null);
    const [calloutOpen, setCalloutOpen] = React.useState(false);

    const storefrontLogo = props.storefrontSettings.footerLogo;
    const globalLogo = props.globalSettings?.footerLogo;
    const effectiveLogo = storefrontLogo || globalLogo;
    const inheritingGlobal = !storefrontLogo && !!globalLogo;

    if (!props.editMode && !effectiveLogo) {
        return null;
    }
    if (!props.editMode) {
        return (
            <div className='storefront-footer-logo-container'>
                <img src={effectiveLogo} className='storefront-footer-logo' alt='Storefront Footer Logo' />
            </div>
        );
    }

    return (
        <div className='storefront-footer-logo-container'>
            <div
                className='storefront-footer-logo-container-edit'
                ref={targetRef}
                onClick={() => setCalloutOpen(true)}
                title='Click to change the footer logo'
            >
                {effectiveLogo
                    ? <img src={effectiveLogo} className='storefront-footer-logo' alt='Storefront Footer Logo' />
                    : <div className='storefront-footer-logo-placeholder'>Footer logo</div>}
            </div>
            {calloutOpen && <Callout
                target={targetRef.current}
                onDismiss={() => setCalloutOpen(false)}
                setInitialFocus
                style={{ padding: '1em' }}
            >
                <h3>Footer Logo Settings</h3>
                <ImageEditor
                    label={'Footer Logo'}
                    value={storefrontLogo}
                    onChange={(v) => props.onStorefrontSettingsChange({ ...props.storefrontSettings, footerLogo: v })}
                    height={75}
                    width={150}
                    imageId={props.storefrontSettings.id}
                    onClear={() => props.onStorefrontSettingsChange({ ...props.storefrontSettings, footerLogo: '' })}
                    imageType='Storefront'
                />
                {inheritingGlobal && <div style={{ maxWidth: 260, fontSize: 12, color: '#605e5c' }}>
                    This storefront has no footer logo of its own, so it shows the organization&rsquo;s global
                    footer logo. Apply an image here to override it for this storefront only.
                </div>}
                {!!storefrontLogo && !!globalLogo && <DefaultButton
                    text='Use global footer logo'
                    iconProps={{ iconName: 'Undo' }}
                    styles={{ root: { marginTop: '0.5em' } }}
                    onClick={() => props.onStorefrontSettingsChange({ ...props.storefrontSettings, footerLogo: '' })}
                />}
            </Callout>}
        </div>
    );
};

export default StorefrontFooterLogoEditor;
